import { Component, Input, OnChanges, OnDestroy, OnInit } from '@angular/core';
import { TicketTabType } from 'src/app/core/enums/ticket-tab-type.enum';
import { TicketSource } from '@sb-tools/typescript/enums/ticket-source.enum';
import { RightPanelStateService } from 'src/app/core/services/right-panel-state.service';
import { TicketRightPanelService } from '../../core/services/ticket-right-panel.service';
import { takeUntil } from 'rxjs/internal/operators/takeUntil';
import { Subject } from 'rxjs/internal/Subject';
import { CommonDataService } from '@sb-tools/typescript/services/common-data.service';
import { RightPanelService } from '../../core/services/right-panel.service';
import { LocalizationLanguage } from '@sb-tools/typescript/enums/localization-language.enum';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { environment } from '@environment';
import { BsModalService } from 'ngx-bootstrap/modal';
import { DocumentViewerModalComponent } from 'src/sb-modules/Frontend-Document-Viewer-Module/components/document-viewer-modal/document-viewer-modal.component';
import { DOCUMENT_VIEWER_MODAL_ID } from 'src/sb-tools/typescript/constants/nested-modal-ids';
import { DetailedTicketDTO } from '../../core/models/dto/detailed-ticket.dto';
import { AppDTO } from '@sb-tools/typescript/models/dto/app.dto';
import { NgClass } from '@angular/common';
import { UserInfoComponent } from '../../shared/components/user-info/user-info.component';
import { TicketNotesComponent } from './ticket-notes/ticket-notes.component';
import { LinkedTicketsComponent } from './linked-tickets/linked-tickets.component';
import { TicketLogComponent } from './ticket-log/ticket-log.component';
import { TicketAccessComponent } from './ticket-access/ticket-access.component';
import { ApplicationComponent } from './application/application.component';
import { EditFieldsComponent } from './edit-fields/edit-fields.component';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { InlineSVGComponent } from '../../../sb-modules/Frontend-Inline-SVG-Module/inline-svg.component';
import { TranslateModule } from '@ngx-translate/core';
import { InboxElementsVisibility } from '@sb-tools/typescript/models/inbox-elements-visibility.model';
import { isInboxElementVisible } from '../../core/utils/is-inbox-element-visible.util';

@Component({
    selector: 'app-ticket-right-panel',
    templateUrl: './ticket-right-panel.component.html',
    styleUrls: ['./ticket-right-panel.component.scss'],
    imports: [
        NgClass,
        UserInfoComponent,
        TicketNotesComponent,
        LinkedTicketsComponent,
        TicketLogComponent,
        TicketAccessComponent,
        ApplicationComponent,
        EditFieldsComponent,
        TooltipModule,
        InlineSVGComponent,
        TranslateModule,
    ],
})
export class TicketRightPanelComponent implements OnChanges, OnInit, OnDestroy {
    @Input() isLoading: boolean;
    @Input() ticket: DetailedTicketDTO;

    apps: AppDTO[] = [];
    currentApp: AppDTO;
    currentAppId: number;
    faqIframeUrl: SafeResourceUrl;
    isFocusNeeded: boolean;
    isNorwegianLanguageUser: boolean;
    tabTypeEnum = TicketTabType;
    ticketSourceEnum = TicketSource;

    private unsubscriber = new Subject<void>();

    constructor(
        public commonDataService: CommonDataService,
        public ticketRightPanelService: TicketRightPanelService,
        private rightPanelStateService: RightPanelStateService,
        private rightPanelService: RightPanelService,
        private sanitizer: DomSanitizer,
        private bsModalService: BsModalService,
    ) {}

    ngOnInit() {
        this.ticketRightPanelService.onActivateTabSubject.pipe(takeUntil(this.unsubscriber)).subscribe((value) => {
            if (this.rightPanelService.isRightPanelOpened) {
                this.rightPanelService.close();
            }

            if (value === TicketTabType.notes && this.isTabVisible(TicketTabType.notes)) {
                if (this.ticketRightPanelService.tabType === TicketTabType.notes) {
                    this.setFocusOnEditor();
                } else {
                    this.toggleTab(TicketTabType.notes);
                }
            }
        });
        this.receiveMessage = this.receiveMessage.bind(this);
        window.addEventListener('message', this.receiveMessage);
        this.faqIframeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
            environment.faqIframeUrl + 'socialboards/socialboards',
        );
    }

    ngOnChanges() {
        this.setExternalAppData();
        this.isFocusNeeded = this.ticketRightPanelService.data?.isFocusNeeded;

        this.isNorwegianLanguageUser =
            this.commonDataService.customerSettings?.language === LocalizationLanguage.norwegian ||
            this.commonDataService.user?.localizationLanguage === LocalizationLanguage.norwegian;

        this.ticketRightPanelService.tabType = this.getDefaultTabType();

        if (!this.ticketRightPanelService.tabType || !this.isTabVisible(this.ticketRightPanelService.tabType)) {
            this.ticketRightPanelService.isRightPanelOpened = false;
        }
    }

    toggleTab(tabType: TicketTabType, app?: AppDTO) {
        if (!app && !this.isTabVisible(tabType)) {
            return;
        }

        if (!this.ticketRightPanelService.isNeedToDiscardUnsavedChanges()) {
            return;
        }

        if (app) {
            this.currentApp = app;
        }

        this.ticketRightPanelService.onToggleRightPanelSubject.next(true);

        if (!app || this.ticketRightPanelService.externalAppId === app?.appId) {
            if (this.ticketRightPanelService.isRightPanelOpened && this.ticketRightPanelService.tabType === tabType) {
                this.ticketRightPanelService.tabType = null;
                this.ticketRightPanelService.isRightPanelOpened = false;
                this.ticketRightPanelService.externalAppId = null;
            } else {
                this.ticketRightPanelService.open({
                    isFocusNeeded: true,
                    ticket: this.ticket,
                    tabType,
                });
                this.ticketRightPanelService.externalAppId = app?.appId;
            }
        } else {
            this.ticketRightPanelService.open({
                isFocusNeeded: true,
                ticket: this.ticket,
                tabType,
            });

            this.ticketRightPanelService.externalAppId = app?.appId;
        }

        this.rightPanelStateService.saveTabState(tabType, app?.appId);
        this.rightPanelStateService.savePanelState(this.ticketRightPanelService.isRightPanelOpened);
    }

    private setExternalAppData() {
        if (!this.commonDataService.apps) {
            return;
        }

        this.getExternalAppId();
        this.apps = this.commonDataService.apps;
        this.apps.forEach((app: AppDTO) => {
            app.isNeedGetInitData = true;
            app.safeInboxUrl = this.sanitizer.bypassSecurityTrustResourceUrl(app.inboxUrl);
        });

        if (this.ticketRightPanelService.tabType === TicketTabType.externalApp) {
            this.currentApp =
                this.apps.find((currentApp) => {
                    return currentApp.appId === this.currentAppId && currentApp.isActive;
                }) || this.getFirstActiveApp();
        }
    }

    private getDefaultTabType() {
        const tabType =
            this.ticketRightPanelService.data?.tabType ||
            this.rightPanelStateService.getTabState() ||
            TicketTabType.notes;

        if (this.isTabVisible(tabType)) {
            return tabType;
        }

        return this.getFirstVisibleTabType();
    }

    isTabVisible(tabType: TicketTabType): boolean {
        switch (tabType) {
            case TicketTabType.ticketAuthor:
                return (
                    !this.commonDataService.user?.isReadOnly &&
                    isInboxElementVisible(this.getInboxElementsVisibility()?.rightPanel?.customerProfile)
                );
            case TicketTabType.notes:
                return (
                    !!this.ticket &&
                    (!this.commonDataService.user?.isReadOnly || this.commonDataService.user?.hasAccessToNotes) &&
                    isInboxElementVisible(this.getInboxElementsVisibility()?.rightPanel?.notes)
                );
            case TicketTabType.linkedTickets:
                return (
                    !!this.ticket &&
                    !this.commonDataService.user?.isReadOnly &&
                    isInboxElementVisible(this.getInboxElementsVisibility()?.rightPanel?.linkedTicketView)
                );
            case TicketTabType.access:
                return (
                    !!this.ticket &&
                    !this.commonDataService.user?.isReadOnly &&
                    this.ticket.source !== TicketSource.chat &&
                    isInboxElementVisible(this.getInboxElementsVisibility()?.rightPanel?.access)
                );
            case TicketTabType.log:
                return (
                    !!this.ticket &&
                    (!this.commonDataService.user?.isReadOnly || this.commonDataService.user?.hasAccessToTicketLog) &&
                    isInboxElementVisible(this.getInboxElementsVisibility()?.rightPanel?.log)
                );
            case TicketTabType.editFields:
                return (
                    !!this.ticket && isInboxElementVisible(this.getInboxElementsVisibility()?.rightPanel?.editFields)
                );
            case TicketTabType.externalApp:
                return Boolean(
                    this.ticket &&
                        (this.ticket.source === TicketSource.form || this.ticket.source === TicketSource.email) &&
                        this.ticket.firstMessageCreator?.email &&
                        this.commonDataService.apps?.some((app) => app.isActive),
                );
            case TicketTabType.faq:
                return this.isNorwegianLanguageUser;
            default:
                return false;
        }
    }

    private getFirstVisibleTabType(): TicketTabType | undefined {
        return [
            TicketTabType.notes,
            TicketTabType.log,
            TicketTabType.ticketAuthor,
            TicketTabType.linkedTickets,
            TicketTabType.access,
            TicketTabType.editFields,
            TicketTabType.externalApp,
            TicketTabType.faq,
        ].find((tabType) => this.isTabVisible(tabType));
    }

    private getExternalAppId() {
        this.currentAppId = this.rightPanelStateService.getExternalAppIdState();
    }

    private getFirstActiveApp(): AppDTO | undefined {
        return this.apps.find((app) => app.isActive);
    }

    private getInboxElementsVisibility(): InboxElementsVisibility | undefined {
        return this.commonDataService.customerSettings?.inboxElementsVisibility;
    }

    private receiveMessage(event: any) {
        if (event.data && event.data.isFaqAttachment) {
            this.bsModalService.show(DocumentViewerModalComponent, {
                initialState: event.data.message.initialState,
                class: 'modal-lg modal-document-viewer',
                id: DOCUMENT_VIEWER_MODAL_ID,
            });
        }
    }

    private setFocusOnEditor() {
        if ($('#nav-content .ql-editor').length > 0) {
            setTimeout(() => {
                $('#nav-content .ql-editor').last().focus();
            }, 300);
        }
    }

    ngOnDestroy() {
        window.removeEventListener('message', this.receiveMessage);
        this.unsubscriber.next();
        this.unsubscriber.complete();
    }
}
